import { useMutation } from '@apollo/client';
import {
  Arrange,
  Container,
  Link,
  Spacer,
  Switch,
  Text,
  Toast,
} from '@loomhq/lens';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { closeModal, openModal } from 'actions/modals';
import { VIDEO_PUBLISH_TOGGLED } from 'constants/events';
import {
  SHARE_UI_MODAL,
  UNPUBLISH_VIDEO_CONFIRMATION_MODAL,
} from 'constants/modalTypes';
import { PUBLISHING_HELP_PAGE } from 'constants/routes';
import { SHOW_FTUX_POST_VIDEO_POPOVER } from 'constants/triggers';
import { VideoVisibility as VisibilityType } from 'constants/visibility';
import UpdateVideoVisibility from 'creators/UpdateVideoVisibility.graphql';
import { track } from 'utilities/analytics';

import useClickOutside from 'hooks/useClickOutside';
import useShouldShowOneTimeFtux from 'hooks/useDestinationFtux';
import { useHasScope } from 'hooks/useHasScopes';
import { useGetSelectedWorkspace } from 'hooks/workspace';

import { VideoCardVideo } from './types';
import { CARD_CAPABILITIES } from '../../constants/videoCard';
import { VIDEO_PUBLISH_ACCESS } from '../../../../shared/constants/scopes';
import { VIDEO_PRIVACY_OWNER } from '../../../../shared/constants/video';

import { ShareUIVideo } from 'components/share-video/share-ui/types';

import styles from './styles.module.less';

type VisibilityPopoverProps = {
  video: VideoCardVideo;
  isPublished: boolean;
  setIsVisibilityPopoverOpen: (isOpen: boolean) => void;
  onVisibilityUpdate?: (visibility: string) => void;
  context: string;
  setIsPublished: (isPublished: boolean) => void;
  showPostVideoFTUX: boolean;
};

const VisibilityPopover = ({
  video,
  isPublished,
  setIsVisibilityPopoverOpen,
  onVisibilityUpdate,
  context,
  setIsPublished,
}: VisibilityPopoverProps): React.ReactElement => {
  const workspace = useGetSelectedWorkspace();
  const [isUpdateVisibilityError, setIsUpdateVisibilityError] = useState(false);
  const { dismissFtuxOnClick: dismissFtuxPostVideoPopover } =
    useShouldShowOneTimeFtux(SHOW_FTUX_POST_VIDEO_POPOVER);
  const ref = useClickOutside(() => {
    setIsVisibilityPopoverOpen(false);
    dismissFtuxPostVideoPopover();
  }, styles.visibilityButton);
  const dispatch = useDispatch();
  const hasPublishAccess = useHasScope(VIDEO_PUBLISH_ACCESS);

  const isPrivate = video.privacy === VIDEO_PRIVACY_OWNER;

  const { id: videoId, currentUserCanEdit } = video;

  const [updateVideoVisibility, { loading }] = useMutation(
    UpdateVideoVisibility,
    {
      onCompleted: data => {
        onVisibilityUpdate &&
          onVisibilityUpdate(data.updateVideoVisibility.video.visibility);
        setIsUpdateVisibilityError(false);
      },
      onError: () => {
        setIsUpdateVisibilityError(true);
        setIsPublished(!isPublished);
      },
      refetchQueries: ['GetWorkspaceUserFromProfileUrl'],
    }
  );

  const updateVisibility = () => {
    const oldVisibility = isPublished
      ? VisibilityType.WORKSPACE
      : VisibilityType.OWNER;

    const newVisibility = isPublished
      ? VisibilityType.OWNER
      : VisibilityType.WORKSPACE;

    updateVideoVisibility({
      variables: {
        videoId,
        visibility: newVisibility,
      },
    });

    setIsPublished(!isPublished);

    track(VIDEO_PUBLISH_TOGGLED, {
      video_id: videoId,
      context_page_path: window.location.pathname,
      toggled_from: oldVisibility,
      toggled_to: newVisibility,
    });

    dispatch(closeModal());
  };

  const onVisibilitySwitchChange = () => {
    // Don't allow updating visibility if visibility
    // update is not complete (don't want someone spamming)
    if (loading) {
      return;
    }

    dismissFtuxPostVideoPopover();

    // in contexts where we only show posted videos, we want
    // to prompt the user to let them know the consequences
    // of unposting and where to post
    if (
      isPublished &&
      CARD_CAPABILITIES['promptOnUnpublish'].includes(context)
    ) {
      dispatch(
        openModal(UNPUBLISH_VIDEO_CONFIRMATION_MODAL, {
          onUnpublish: updateVisibility,
        })
      );
    } else {
      updateVisibility();
    }

    setIsUpdateVisibilityError(false);
  };

  const message = !(currentUserCanEdit && hasPublishAccess)
    ? isPublished
      ? 'Only an editor of this video can unpost it.'
      : 'Only an editor of this video can post it.'
    : isPrivate
    ? `This video is not visible to ${workspace.name}. Change its privacy setting in order to enable posting.`
    : `Anyone in ${workspace.name} will be able to find this video once it is posted.`;

  return (
    <div
      onClick={e => {
        e.stopPropagation();
      }}
      ref={ref}
    >
      <Container
        padding="medium"
        backgroundColor="background"
        borderSide="all"
        radius="medium"
        shadow="medium"
        width="17.5rem"
      >
        <Arrange justifyContent="space-between">
          <Text fontWeight="bold">Post to {workspace.name}</Text>
          <Switch
            onChange={onVisibilitySwitchChange}
            isActive={isPublished}
            isDisabled={isPrivate || !currentUserCanEdit || !hasPublishAccess}
          />
        </Arrange>
        <Spacer top="medium" />
        <Text variant="body" htmlTag="p" color="bodyDimmed">
          {message}
        </Text>
        <Spacer top="medium">
          {!isPublished && isPrivate ? (
            <Link
              onClick={() =>
                dispatch(
                  openModal(SHARE_UI_MODAL, { video: video as ShareUIVideo })
                )
              }
            >
              Change privacy setting
            </Link>
          ) : (
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={PUBLISHING_HELP_PAGE}
            >
              More Info
            </Link>
          )}
        </Spacer>
      </Container>

      <Toast
        isOpen={Boolean(isUpdateVisibilityError)}
        onCloseClick={() => setIsUpdateVisibilityError(false)}
        zIndex={5}
      >
        Failed to update video visibility.
      </Toast>
    </div>
  );
};

export default VisibilityPopover;
